import { motion, useScroll, useTransform } from "framer-motion";
import { SakuraMark } from "@/components/SakuraMark";
import { IMAGES, waLink, WA_MESSAGES, scrollToId } from "@/lib/site";
import { ArrowDown, MessageCircle } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const MaskLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

export const Hero = () => {
    const { scrollY } = useScroll();
    const yImg = useTransform(scrollY, [0, 700], [0, 90]);
    const yText = useTransform(scrollY, [0, 700], [0, -40]);

    return (
        <section
            id="top"
            className="relative overflow-hidden"
            data-testid="hero-section"
        >
            <div
                aria-hidden="true"
                className="absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-sky-300/50 blur-3xl"
            />
            <div
                aria-hidden="true"
                className="absolute -left-32 bottom-0 h-[380px] w-[380px] rounded-full bg-sakura-300/60 blur-3xl"
            />

            <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 pb-16 pt-36 sm:px-8 lg:min-h-screen lg:grid-cols-[1.1fr_0.9fr] lg:gap-8 lg:pb-24 lg:pt-40">
                <motion.div style={{ y: yText }}>
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
                        className="mb-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.32em] text-sky-600"
                    >
                        <SakuraMark size={22} />
                        Handcrafted personalized gifts
                    </motion.p>

                    <h1 className="font-display text-[clamp(2.9rem,8vw,5.9rem)] font-medium leading-[1.02] tracking-tight text-sakura-900">
                        <MaskLine delay={0.2}>Personalized gifts,</MaskLine>
                        <MaskLine delay={0.35}>
                            in{" "}
                            <em className="font-display italic text-sakura-600">
                                full bloom.
                            </em>
                        </MaskLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
                        className="mt-7 max-w-xl text-base leading-relaxed text-sakura-800/90 md:text-lg"
                    >
                        Custom mugs, t-shirts, mouse pads, key rings & mason
                        jars — printed with the names, photos and moments that
                        matter. Delivered anywhere in South Africa.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <a
                            data-testid="hero-whatsapp-cta"
                            href={waLink(WA_MESSAGES.general)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex items-center gap-2.5 rounded-full bg-sky-600 px-7 py-4 text-sm font-semibold text-white shadow-[0_14px_34px_rgba(107,133,168,0.4)] hover:bg-sky-700 hover:shadow-[0_10px_26px_rgba(81,104,135,0.45)] transition-all duration-200"
                        >
                            <MessageCircle size={18} />
                            Order on WhatsApp
                        </a>
                        <button
                            data-testid="hero-special-cta"
                            onClick={() => scrollToId("special")}
                            className="group flex items-center gap-2 rounded-full border border-sakura-600/40 px-7 py-4 text-sm font-semibold text-sakura-700 hover:bg-sakura-600 hover:text-white transition-colors duration-200"
                        >
                            This Month's Special
                            <ArrowDown
                                size={16}
                                className="group-hover:translate-y-0.5 transition-transform duration-200"
                            />
                        </button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.05 }}
                        className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-sakura-800/80"
                    >
                        {[
                            "Mugs R100 · Mason jars R130",
                            "PUDO · PAXI · The Courier Guy",
                            "Courier quoted separately",
                        ].map((t) => (
                            <span key={t} className="flex items-center gap-2">
                                <SakuraMark size={16} />
                                {t}
                            </span>
                        ))}
                    </motion.div>
                </motion.div>

                <motion.div
                    style={{ y: yImg }}
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
                    className="relative mx-auto w-full max-w-md lg:max-w-none"
                >
                    <div className="overflow-hidden rounded-b-[2.5rem] rounded-t-[999px] border-[6px] border-white/80 shadow-[0_30px_80px_rgba(162,182,208,0.35)]">
                        <img
                            src={IMAGES.heroWork}
                            alt="Custom printed mugs made by Cherry Blossom Gifts"
                            className="h-[420px] w-full object-cover md:h-[540px] lg:h-[600px]"
                            loading="eager"
                        />
                    </div>
                    <motion.a
                        href={waLink(WA_MESSAGES.ramadhaan)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hero-float-special-card"
                        animate={{ y: [0, -10, 0] }}
                        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute -left-3 bottom-10 flex items-center gap-3 rounded-2xl border border-sakura-400/50 bg-white/80 px-4 py-3 shadow-[0_16px_40px_rgba(162,182,208,0.3)] backdrop-blur-xl hover:bg-white transition-colors duration-200 sm:-left-8"
                    >
                        <SakuraMark size={30} />
                        <span>
                            <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-sakura-600">
                                This Month's Special
                            </span>
                            <span className="block font-display text-base font-semibold text-sakura-900">
                                Ramadhaan Gift Sets
                            </span>
                        </span>
                    </motion.a>
                    <div className="absolute -right-2 top-10 rotate-3 rounded-full bg-sky-700 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white shadow-lg sm:right-4">
                        Mugs R100 each
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default Hero;
